import './styles.scss';

import { useContext, useEffect, useMemo, useState } from 'react';

import { BackgroundMessage } from '../../../../../components/background-message';
import { ReportFilterMultiSelect } from '../../../../../components/report-filter-buttons/report-filter-multi-select';
import Select from '../../../../../components/select/select';
import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import { PORTFOLIO_CONTRACTS } from '../../../../../constants/reports/reports-filters';
import { PORTFOLIO_MESSAGES } from '../../../../../constants/reports/reports-messages';
import { GlobalContext } from '../../../../../providers/global-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import { PositionsFiltersContext } from '../../../../../providers/positions/positions-filters-provider';
import { PositionsContext } from '../../../../../providers/positions/positions-provider';
import { IAccountOption } from '../../../../../providers/venue-provider';
import Utils from '../../../../../utils/immersive_utils';
import Orders from '../orders';

import PositionTable from './position-table';

interface PositionsBodyProps {
  accountOption: IAccountOption;
}

const values = ['Notional', 'PV', 'Delta', 'Theta', 'gamma', 'Vega', 'MarkPrice', 'Spot'];

const ORDER_CONTRACTS = ['Perpetual', 'Future', 'Option', 'Cash'];

const HEADER = [
  'Position', //notions
  'PV',
  'Delta',
  'Theta',
  'Gamma',
  'Vega',
  'MarkPrice',
  'Spot',
];

const PositionsBody = ({ accountOption }: PositionsBodyProps) => {
  const [data, setData] = useState<any>(null);
  const [lastUpdatePositions, setLastUpdatePositions] = useState<string>('');

  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    filters: {
      token,
      tokenList,
      errorPosition,
      errorOrders,
      positionsInstType,
      setPositionsInstType,
      setToken,
      setTokenList,
      setErrorPosition,
    },
  } = useContext(PositionsFiltersContext);
  const { aggregatedData, getAggregatedData, getDataFromAccount, selectedDataDisplayOption } =
    useContext(PortfolioManagementContext);
  const { isLoadingPositions, isLoadingOrders, getData } = useContext(PositionsContext);

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const { POSITIONS } = PORTFOLIO_CONTRACTS;

  function groupByHierarchy(data: any[], allowedContracts: string[], token: string) {
    const groupedData: Record<string, any> = {};

    const sumArrays = (arrays: number[][]) =>
      arrays.reduce((acc, arr) => {
        arr.forEach((value, index) => {
          acc[index] = (acc[index] || 0) + value;
        });
        return acc;
      }, []);

    const processValues = (values: any[]) => values.map((value) => parseFloat(value) || 0);

    data.forEach((item) => {
      const { Exchange, Underlying, Contract, Expiry } = item;

      const isContractAllowed = allowedContracts.includes(Contract);
      const isTokensAllowed =
        typeof Underlying === 'string' && Underlying.length > 0 && token.includes(Underlying);
      const isExpiryAllowed = true;

      if (isContractAllowed && isExpiryAllowed && isTokensAllowed) {
        if (!groupedData[Exchange]) {
          groupedData[Exchange] = {
            id: Exchange,
            label: Exchange,
            children: [],
            values: processValues(item.values),
          };
        } else {
          groupedData[Exchange].values = sumArrays([
            groupedData[Exchange].values,
            processValues(item.values),
          ]);
        }

        const exchangeNode = groupedData[Exchange];

        let underlyingNode = exchangeNode.children.find(
          (child: any) => child.id === `${Exchange}_${Underlying}`,
        );
        if (!underlyingNode) {
          underlyingNode = {
            id: `${Exchange}_${Underlying}`,
            label: Underlying,
            children: [],
            values: processValues(item.values),
          };
          exchangeNode.children.push(underlyingNode);
        } else {
          underlyingNode.values = sumArrays([underlyingNode.values, processValues(item.values)]);
        }

        if (Contract === 'OPTION' || Contract === 'FUTURE' || Contract === 'PERPETUAL') {
          let contractNode = underlyingNode.children.find(
            (child: any) => child.id === `${Exchange}_${Underlying}_${Contract}`,
          );
          if (!contractNode) {
            contractNode = {
              id: `${Exchange}_${Underlying}_${Contract}`,
              label: POSITIONS.CONTRACT_KEYS.find((it) => it.value === Contract)?.label,
              children: [],
              values: processValues(item.values),
            };

            const currentArr = [...underlyingNode.children, contractNode];

            currentArr.sort((a: any, b: any) => {
              return ORDER_CONTRACTS.indexOf(a.label) - ORDER_CONTRACTS.indexOf(b.label);
            });

            underlyingNode.children = currentArr;
          } else {
            contractNode.values = sumArrays([contractNode.values, processValues(item.values)]);
          }

          if (Expiry && (Contract !== 'FUTURE' || Contract === 'PERPETUAL')) {
            let expiryNode = contractNode.children.find(
              (child: any) => child.id === `${Exchange}_${Underlying}_${Contract}_${Expiry}`,
            );
            if (!expiryNode) {
              expiryNode = {
                id: `${Exchange}_${Underlying}_${Contract}_${Expiry}`,
                label: Expiry,
                children: [],
                values: processValues(item.values),
              };
              contractNode.children.push(expiryNode);
            } else {
              expiryNode.values = sumArrays([expiryNode.values, processValues(item.values)]);
            }

            expiryNode.children.push(item);
          } else {
            contractNode.children.push(item);
          }

          if (Contract === 'OPTION') {
            const getTime = (label: any): number => {
              if (typeof label !== 'string' || label.length === 0) return NaN;

              const nativeTime = new Date(label).getTime();
              if (!Number.isNaN(nativeTime)) return nativeTime;

              const parsed = Utils.parseDate(label);
              return parsed ? parsed.getTime() : NaN;
            };

            contractNode.children?.sort((a: any, b: any) => {
              const timeA = getTime(a?.label);
              const timeB = getTime(b?.label);

              const isValidA = !Number.isNaN(timeA);
              const isValidB = !Number.isNaN(timeB);

              if (!isValidA && !isValidB) return 0;
              if (!isValidA) return 1;
              if (!isValidB) return -1;

              return timeA - timeB;
            });
          }
        } else {
          let contractNode = underlyingNode.children.find(
            (child: any) => child.id === `${Exchange}_${Underlying}_${Contract}`,
          );
          if (!contractNode) {
            contractNode = {
              id: `${Exchange}_${Underlying}_${Contract}`,
              label: Contract.charAt(0).toUpperCase() + Contract.slice(1).toLowerCase(),
              children: [],
              values: processValues(item.values),
            };
            underlyingNode.children.push(contractNode);
          } else {
            contractNode.values = sumArrays([contractNode.values, processValues(item.values)]);
          }

          if (Expiry) {
            let expiryNode = contractNode.children.find(
              (child: any) => child.id === `${Exchange}_${Underlying}_${Contract}_${Expiry}`,
            );
            if (!expiryNode) {
              expiryNode = {
                id: `${Exchange}_${Underlying}_${Contract}_${Expiry}`,
                label: Expiry,
                children: [],
                values: processValues(item.values),
              };
              contractNode.children.push(expiryNode);
            } else {
              expiryNode.values = sumArrays([expiryNode.values, processValues(item.values)]);
            }

            expiryNode.children.push(item);
          } else {
            contractNode.children.push(item);
          }
        }
      }
    });

    const newData = Object.values(groupedData);

    /* remove position at exchange */
    newData.map((itemExchange: any) => {
      const shifted = JSON.parse(JSON.stringify(itemExchange.values));

      return {
        ...itemExchange,
        values: ['', ...shifted],
      };
    });

    return newData;
  }

  const groupedData = useMemo(() => {
    if (portfolioCurrentTab !== PORTFOLIO_TABS_NAME.positionsAndOrders) {
      return [];
    }

    let newData: any[] = [];

    const processAndGroupData = (filteredData: any[], exchange: string) => {
      const uniqueTokens: string[] = Array.from(
        new Set(
          filteredData
            .map((item: any) => item['Underlying']?.replace('USD', ''))
            .filter((item: any) => item.length > 0),
        ),
      );

      const handleTokenList = (tokens: string[]) => {
        const getLabel = (token: string) => {
          let label = token;

          const spplitTokenLabel = (token: string, suffix: string) => {
            if (token.includes(suffix)) {
              return `${token.replace(suffix, '')} ${suffix}`;
            }
            return null;
          };

          const suffixs = ['USDC', 'USDT', 'USD'];

          suffixs.some((suffix) => {
            const nLabel = spplitTokenLabel(label, suffix);
            if (nLabel) {
              label = nLabel;
              return true;
            }
            return false;
          });

          return label;
        };

        const sortedTokens = Utils.naturalSort(tokens).filter((token) => token.length > 0);

        setTokenList(
          sortedTokens.map((tk) => ({
            value: tk,
            label: getLabel(tk),
            isVisible: true,
          })),
        );
      };

      handleTokenList(uniqueTokens);

      const processedData = filteredData.map((element: any) => ({
        ...element,
        Underlying: element['Underlying']?.replace('USD', ''),
        Exchange: exchange,
        id: `${element['ID']}_${Math.floor(Math.random() * 90000) + 10000}`,
        label: element['ID'],
        values: values.map((val: string) => {
          const floatVal = parseFloat(element[val] || '0');
          if (val === 'Notional' && element['BuySell'] === 'SELL') {
            return floatVal * -1;
          }
          return floatVal;
        }),
        Contract: element['Contract'],
      }));

      const allowedContracts = POSITIONS.CONTRACT_VALUES.filter((type) =>
        positionsInstType.includes(type),
      );

      return groupByHierarchy(processedData, allowedContracts, token);
    };

    if (data) {
      if (isDataDisplayPerAccount) {
        // Per account
        const filteredData = data.filter(
          (element: any) => element['Underlying'] !== 'USDC' && element['Underlying'] !== 'USDT',
        );
        const exchange = Utils.capitalizeFirstLetter(accountOption.exchange);
        newData = [processAndGroupData(filteredData, exchange)];
      } else {
        // Aggregated
        const newDataArray = [];

        if (data.deribit) {
          const filteredDataDeribit = data.deribit.filter(
            (element: any) => element['Underlying'] !== 'USDC' && element['Underlying'] !== 'USDT',
          );
          newDataArray.push(processAndGroupData(filteredDataDeribit, 'Deribit'));
        }

        if (data.okx) {
          const filteredDataOkx = data.okx.filter(
            (element: any) => element['Underlying'] !== 'USDC' && element['Underlying'] !== 'USDT',
          );
          newDataArray.push(processAndGroupData(filteredDataOkx, 'OKX'));
        }

        newData = newDataArray;
      }
    }

    if (newData?.length > 0) {
      const sortByExpiry = (items: any[]): any[] => {
        return items
          .map((item) => {
            if (item.children && item.children.length > 0) {
              return {
                ...item,
                children: sortByExpiry(item.children),
              };
            }
            return item;
          })
          .sort((a, b) => {
            if (a.Strike && b.Strike) {
              return parseInt(a.Strike) - parseInt(b.Strike);
            }
            return 0;
          });
      };

      const sorted = newData.map((itemExchange: any) => {
        return sortByExpiry(itemExchange);
      });

      return sorted;
    }

    return newData;
  }, [data, token, positionsInstType, isDataDisplayPerAccount]);

  useEffect(() => {
    if (
      isLoadingPositions ||
      !isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.positionsAndOrders
    )
      return;

    const dataAccount = getDataFromAccount('POSTIONS', accountOption);

    if (!dataAccount) {
      getData(accountOption);
      setData(null);
      setErrorPosition(false);
      return;
    }

    if (dataAccount.error) {
      setErrorPosition(true);
      setData(null);
      return;
    }

    if (dataAccount.data) {
      setData(dataAccount.data);
      setLastUpdatePositions(dataAccount.lastUpdate);
      setErrorPosition(false);
      return;
    }

    setData(null);
    setErrorPosition(false);
  }, [accountOption, isLoadingPositions, portfolioCurrentTab, isDataDisplayPerAccount]);

  const callAggregatedAccountsPositionsAndOrdersData = () => {
    const AggregatedAccountsFilteredByExchangeDeribit = aggregatedData.accounts.filter(
      (account) => account.exchange === 'deribit',
    );
    const AggregatedAccountsFilteredByExchangeOkx = aggregatedData.accounts.filter(
      (account) => account.exchange === 'okx',
    );

    if (AggregatedAccountsFilteredByExchangeDeribit.length > 0) {
      getData(AggregatedAccountsFilteredByExchangeDeribit, aggregatedData.accounts);
    }
    if (AggregatedAccountsFilteredByExchangeOkx.length > 0) {
      getData(AggregatedAccountsFilteredByExchangeOkx, aggregatedData.accounts);
    }
  };

  useEffect(() => {
    if (
      isLoadingPositions ||
      isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.positionsAndOrders
    )
      return;

    const dataAggregatedAccounts = getAggregatedData('POSTIONS');

    if (!dataAggregatedAccounts) {
      setData(null);
      setErrorPosition(false);

      callAggregatedAccountsPositionsAndOrdersData();
      return;
    }

    if (dataAggregatedAccounts.error.deribit || dataAggregatedAccounts.error.okx) {
      setData(null);
      setErrorPosition(true);
      return;
    }

    if (dataAggregatedAccounts.data) {
      setData(dataAggregatedAccounts.data);
      setLastUpdatePositions(dataAggregatedAccounts.lastUpdate);
      setErrorPosition(false);
      return;
    }

    setData(null);
    setErrorPosition(false);
  }, [portfolioCurrentTab, isLoadingPositions, isDataDisplayPerAccount]);

  const renderTokenSelect = useMemo(() => {
    if (portfolioCurrentTab !== PORTFOLIO_TABS_NAME.positionsAndOrders) return null;

    const visibleTokenList = tokenList.filter((tk: any) => tk.isVisible && tk.value.length > 0);

    const selectedToken =
      visibleTokenList.find((tk: any) => tk.label.length > 0 && tk.label === token) ||
      visibleTokenList[0];

    const tokenListMapp = visibleTokenList.map((tk: any) => ({
      value: tk.value,
      name: tk.label,
    }));

    if (selectedToken) {
      setToken(selectedToken.value);
    } else {
      return null;
    }

    return (
      <Select
        defaultValue={{ value: selectedToken?.value, name: selectedToken?.label }}
        values={tokenListMapp}
        onChange={(val: string) => {
          setToken(val);
        }}
        className="token-select"
        label="Token"
      />
    );
  }, [tokenList, token]);

  const renderTable = useMemo(() => {
    let hasAllData = false;
    const hasOnFilterData = groupedData[0]?.length > 0;

    if (isDataDisplayPerAccount) {
      hasAllData = data && data.length > 0;
    } else {
      hasAllData =
        (data?.deribit && data?.deribit?.length > 0) || (data?.okx && data?.okx?.length > 0);
    }

    return (
      <div className="data-table">
        <PositionTable
          data={groupedData}
          header={HEADER}
          lastUpdatePositions={lastUpdatePositions}
          hasAllData={hasAllData}
          hasOnFilterData={hasOnFilterData}
        />
      </div>
    );
  }, [data, groupedData, isDataDisplayPerAccount]);

  const renderOrders = useMemo(() => {
    return (
      <div className="data-table">
        <Orders accountOption={accountOption} />
      </div>
    );
  }, [data, token]);

  if (!isLoadingPositions && !isLoadingOrders && (errorPosition || errorOrders)) {
    return (
      <BackgroundMessage
        message={PORTFOLIO_MESSAGES.ERROR_REQUEST_POSITIONS_ORDERS}
        type="error"
        button={{
          title: PORTFOLIO_MESSAGES.ERROR_REQUEST_POSITIONS_ORDERS.button,
          onClick: () =>
            isDataDisplayPerAccount
              ? getData(accountOption)
              : callAggregatedAccountsPositionsAndOrdersData(),
        }}
      />
    );
  }

  const isRenderFilters = !!data && data.length > 0;

  return (
    <div className="pos-body-container">
      {isRenderFilters && (
        <div className="pos-body-header">
          <div className="select-token-container">{renderTokenSelect}</div>

          <ReportFilterMultiSelect
            titleFilter="Inst Type"
            dataArr={POSITIONS.CONTRACT_KEYS}
            selected={positionsInstType}
            setSelected={setPositionsInstType}
          />
        </div>
      )}
      <div className="container">
        {renderTable}
        {renderOrders}
      </div>
    </div>
  );
};

export default PositionsBody;
